import React, {useEffect, useMemo, useRef, useState} from 'react'
import { Table, TableContainer, ThTable, ThTableItem } from './SmrtSearchTableNew'
import { useLexicon } from '../../hooks/useLexicon'
import { extractFirstCharacters } from '../../helpers';
import { useParameterLexicon } from '../../hooks/useParameterLexicon';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { $apiV2 } from '../../config/api';
import { useNotification } from '../notification/useNotification';
import { NotificationList } from '../notification/NotificationList';

import { ReactComponent as SortAscIcon } from "../../images/icons/sortasc_icon.svg";
import { ReactComponent as SortDescIcon } from "../../images/icons/sort_desc.svg";
import { ReactComponent as SortDefaultIcon } from "../../images/icons/sort_default.svg";
import play from "../../images/ic_play.svg";
import playActive from "../../images/ic_play_active.svg";
import playActiveBlue from "../../images/ic_play_active_blue.svg";
import downloadIcon from "../../images/ic_download.svg";
import downloadIconBlue from "../../images/download_blue.svg";
import emptySearch from "../../images/ic_empty.svg";


import Moment from 'react-moment';
import { EmptyState } from '../../style/Tournaments';
import { Oval } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Score } from './Matches';
import { TeamMatches, TeamName } from '../../style/MatchTable';
import { openVideoList } from '../../helpers/service';

const UnavailableVideoLabel = styled.span`
    color: ${(props) => props.theme.textMuted};
    cursor: not-allowed;
    font-family: "SatoshiMedium";
    font-size: 10px;
    font-weight: 500;
    line-height: 18px;
    position: absolute;
    right: ${(props) => props.$withDownload ? '34px' : '10px'};
    top: 50%;
    transform: translateY(-50%);
`;

export const PlayerStatTable = ({ data, loading, handleSortTable, tableTypeSorting, playerName, playerId }) => {
    const l = useLexicon();
    const { t } = useTranslation();
    const { getPlayerParameterLexiconId } = useParameterLexicon();
    const tableRef = useRef(null);
    const navigate = useNavigate();

    const canDownloadReport = useSelector((state) => state.user.canDownloadReport);
    const [downloadingReportMatchId, setDownloadingReportMatchId] = useState(null);
    const { notifications, addNotification, removeNotification } = useNotification();

    const handleDownloadReport = async (matchId) => {
        const id = Number(matchId);
        if (!Number.isFinite(id) || downloadingReportMatchId !== null) return;

        const requestedPlayerId = Number(playerId);
        const hasPlayer = Number.isFinite(requestedPlayerId) && requestedPlayerId > 0;

        const notifId = `dl-player-match-report-${id}`;
        setDownloadingReportMatchId(id);
        addNotification("Download in progress...", "info", { id: notifId, duration: 0 });

        try {
            const response = await $apiV2.post(
                `/platform/match/${id}/export/report/pdf`,
                hasPlayer ? { player_id: requestedPlayerId } : {},
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = hasPlayer
                ? `player-match-report-${requestedPlayerId}-${id}.pdf`
                : `match-report-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Match report download failed", error);
            addNotification("Error, please try again later.", "error");
        } finally {
            removeNotification(notifId);
            setDownloadingReportMatchId((prev) => Number(prev) === id ? null : prev);
        }
    };

    const tableHeadNames = useMemo(() => {
        if (data?.length > 0) {
            const dataWithParams = data.find(item => item?.parameters?.length > 0);
            if (dataWithParams) {
                return dataWithParams.parameters;
            }
            return []
        }
        return [];
    }, [data]);

    const getParameterDisplayName = (parameter) => {
        const fallbackName =
            typeof parameter === 'string'
                ? parameter
                : parameter?.parameter_name || parameter?.name || '';
        if (!fallbackName) return '';
        const lexiconId = getPlayerParameterLexiconId(parameter);
        if (lexiconId) {
            return l(lexiconId) || fallbackName;
        }
        return fallbackName;
    };

    const isCanClickStat = (parameter) => {
        return (
            parameter?.value === 0 ||
            parameter?.markers?.length === 0 ||
            parameter?.parameter_name?.includes("%") ||
            parameter?.parameter_name?.includes("Mins Played") ||
            parameter?.parameter_name?.includes("Clean Sheets")
        );
    };

    return (
        <>
        <TableContainer ref={tableRef}>
            <Table>
                <ThTable>
                    <tr>
                        <th className='sticky name'>{l(7)}</th>
                        {tableHeadNames?.map(item => {
                            const displayName = getParameterDisplayName(item);
                            return (
                            <ThTableItem
                                title={displayName}
                                key={item.parameter_id}
                                onClick={() => handleSortTable(item)}
                            >
                                {extractFirstCharacters(displayName)}
                                {
                                    tableTypeSorting.type === 'asc' && tableTypeSorting.param === item.parameter_id ? (
                                        <SortAscIcon />
                                    ) : tableTypeSorting.type === 'desc' && tableTypeSorting.param === item.parameter_id ? (
                                        <SortDescIcon />
                                    ) : (
                                        <SortDefaultIcon />
                                    )
                                }
                            </ThTableItem>
                            );
                        })}
                    </tr>
                </ThTable>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={tableHeadNames?.length < 10 ? tableHeadNames?.length + 2 : tableHeadNames?.length > 10 ? 12 : 2}>
                                <Oval
                                    visible={true}
                                    height="80"
                                    width="80"
                                    secondaryColor={
                                        localStorage.getItem("theme") === "light"
                                            ? "#4E5255"
                                            : "#CFDAE6"
                                    }
                                    color={
                                        localStorage.getItem("theme") === "light"
                                            ? "#2363F7"
                                            : "#4fa94d"
                                    }
                                    ariaLabel="oval-loading"
                                    wrapperStyle={{ height: '50vh', alignItems: 'center', justifyContent: 'center' }}
                                    wrapperClass=""
                                />
                            </td>
                        </tr>
                    ) : data?.length > 0 ? data?.map((row, i) => {
                        const {
                            parameters,
                            id,
                            away_team,
                            home_team,
                            date,
                            home_team_id,
                            away_team_id,
                            competition,
                            videos,
                            round
                        } = row;
                        const hasVideo = Array.isArray(videos) && videos.length > 0;
                        const isShouldColor = i % 2 === 0;
                        return (
                            <tr key={id} className={isShouldColor ? 'tr-colored' : ''}>
                                <td className='sticky name'>
                                    <TeamMatches className="playerStatColumnHeight">
                                        <TeamName className="matchPlayerStat">
                                            <Score className="scorePlayerStat">
                                                <h3>
                                                    <Moment format="DD.MM.YYYY">{date}</Moment>
                                                    <span
                                                        title={competition}
                                                    >
                                                        &nbsp;{competition}&nbsp;-&nbsp;{round}
                                                    </span>
                                                </h3>

                                                <h5
                                                    data-tooltip-id={`match-${id}`}
                                                    className="playerStatTitle"
                                                    title={`${home_team} - ${away_team}, ${competition}`}
                                                >
                                                    <span
                                                        onClick={() => window.open(`/tournaments/team/${home_team_id}`)}
                                                    >
                                                        {home_team}
                                                    </span>
                                                    &nbsp;-&nbsp;
                                                    <span
                                                        onClick={() => window.open(`/tournaments/team/${away_team_id}`)}
                                                    >
                                                        {away_team}
                                                    </span>

                                                    {hasVideo ? (
                                                        <img
                                                            src={
                                                                localStorage.getItem("theme") === "light"
                                                                    ? playActiveBlue
                                                                    : playActive
                                                            }
                                                            alt={play}
                                                            onClick={() => {
                                                                window.open(
                                                                    `/tournaments/match/video/${id}`,
                                                                    "_blank"
                                                                );
                                                            }}
                                                            onContextMenu={() => {
                                                                window.open(
                                                                    `/tournaments/match/video/${id}`,
                                                                    "_blank"
                                                                );
                                                            }}
                                                            style={{ cursor: "pointer", right: canDownloadReport === true ? 34 : 10, position: 'absolute' }}
                                                            title={l(1019)}
                                                        />
                                                    ) : (
                                                        <UnavailableVideoLabel $withDownload={canDownloadReport === true}>
                                                            N/A
                                                        </UnavailableVideoLabel>
                                                    )}
                                                    {canDownloadReport === true && (
                                                        <img
                                                            src={
                                                                localStorage.getItem("theme") === "light"
                                                                    ? downloadIconBlue
                                                                    : downloadIcon
                                                            }
                                                            alt="download"
                                                            title={
                                                                Number(downloadingReportMatchId) === Number(id)
                                                                    ? l(312)
                                                                    : t("statistics.downloadReport")
                                                            }
                                                            onClick={() => handleDownloadReport(id)}
                                                            style={{
                                                                cursor: downloadingReportMatchId !== null ? "not-allowed" : "pointer",
                                                                right: 10,
                                                                position: 'absolute',
                                                                opacity: downloadingReportMatchId !== null ? 0.5 : 0.75,
                                                                width: 16,
                                                                height: 16,
                                                            }}
                                                        />
                                                    )}
                                                </h5>
                                            </Score>
                                        </TeamName>
                                    </TeamMatches>
                                </td>
                                {parameters?.length > 0 ? parameters?.map((param, idx) => {
                                    const isCanClickParam = isCanClickStat(param);
                                    return (
                                        <td
                                            className='values'
                                            key={`${param.parameter_id}-${idx}`}
                                            style={
                                                isCanClickParam
                                                    ? { cursor: "not-allowed" }
                                                    : { cursor: "pointer" }
                                            }
                                            onClick={() => {
                                                if (!isCanClickParam) {
                                                    const displayName = getParameterDisplayName(param);
                                                    openVideoList(param.markers?.join(","), `${displayName} - ${playerName}`)
                                                }
                                            }}
                                            onContextMenu={(e) => {
                                                e.preventDefault()
                                                if (!isCanClickParam) {
                                                    const displayName = getParameterDisplayName(param);
                                                    openVideoList(param.markers?.join(","), `${displayName} - ${playerName}`)
                                                }
                                            }

                                            }
                                        >
                                            {
                                                Number.isInteger(param.value)
                                                    ? param.value?.toLocaleString()
                                                    : param.value?.toFixed(1)
                                            }
                                        </td>
                                    )
                                }) : <td colSpan={tableHeadNames?.length}></td>}
                            </tr>
                        )
                    }) : (
                        <tr>
                            <td colSpan={2}>
                                <EmptyState style={{ height: '50vh', padding: 0 }}>
                                    <img src={emptySearch} alt={emptySearch} />
                                    <h3>{l(314)}</h3>
                                </EmptyState>
                            </td>
                        </tr>
                    )
                    }
                </tbody>
            </Table>
        </TableContainer >
        <NotificationList notifications={notifications} onClose={removeNotification} />
        </>
    )
}
